/**
 * Dialog for exporting a Project to PDF with section-level controls.
 * Lets the downloader choose which parts of the project to include — useful
 * when the team wants to share an external-facing summary without internal
 * comments or sensitive research pages.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";

export default function ExportProjectPdfDialog({ projectId, projectTitle, triggerClassName, triggerLabel = "PDF" }) {
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Section toggles
  const [includeDescription, setIncludeDescription] = useState(true);
  const [includeTasks, setIncludeTasks] = useState(true);
  const [includePages, setIncludePages] = useState(false);
  const [includeComments, setIncludeComments] = useState(true);

  // Per-row selection (default: all selected when section is on)
  const [taskSel, setTaskSel] = useState({});  // taskId -> true
  const [pageSel, setPageSel] = useState({});  // pageId -> true

  // Lazy-load tasks + pages list when opened
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get(`/tasks`, { params: { project_id: projectId } }).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/pages`).catch(() => ({ data: [] })),
    ]).then(([t, p]) => {
      setTasks(t.data || []);
      setPages(p.data || []);
      setTaskSel(Object.fromEntries((t.data || []).map((x) => [x.id, true])));
      setPageSel(Object.fromEntries((p.data || []).map((x) => [x.id, true])));
    }).finally(() => setLoading(false));
  }, [open, projectId]);

  const allTaskIds = tasks.map((t) => t.id);
  const allPageIds = pages.map((p) => p.id);
  const selectedTaskIds = allTaskIds.filter((id) => taskSel[id]);
  const selectedPageIds = allPageIds.filter((id) => pageSel[id]);

  const toggleAll = (setter, ids, value) => {
    setter(Object.fromEntries(ids.map((id) => [id, value])));
  };

  const download = () => {
    if (!includeDescription && !includeTasks && !includePages && !includeComments) {
      toast.error("Pick at least one section to include");
      return;
    }
    const params = new URLSearchParams();
    params.set("include_description", String(includeDescription));
    params.set("include_tasks", String(includeTasks));
    params.set("include_pages", String(includePages));
    params.set("include_comments", String(includeComments));
    if (includeTasks && selectedTaskIds.length > 0 && selectedTaskIds.length < allTaskIds.length) {
      params.set("task_ids", selectedTaskIds.join(","));
    }
    if (includePages && selectedPageIds.length > 0 && selectedPageIds.length < allPageIds.length) {
      params.set("page_ids", selectedPageIds.join(","));
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/export-pdf?${params.toString()}`;
    window.open(url, "_blank", "noreferrer");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className={triggerClassName || "inline-flex items-center gap-1.5 h-9 px-3 border border-slate-200 hover:border-[#0a2540] hover:text-[#0a2540] rounded-sm text-sm text-slate-600"}
          title="Export project to PDF — choose what to include"
          data-testid="btn-export-project-pdf"
        >
          <FileDown size={14} /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="dialog-export-project-pdf">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Export project to PDF</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Pick what to include in the PDF for <span className="font-semibold text-[#0a2540]">{projectTitle}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {/* Sections */}
          <div className="border border-slate-200 rounded-sm p-3 space-y-2.5">
            <div className="gc-label mb-1.5">Sections</div>
            <SectionToggle
              testId="toggle-description"
              checked={includeDescription}
              onChange={setIncludeDescription}
              label="Description"
              hint="Project description body"
            />
            <SectionToggle
              testId="toggle-tasks"
              checked={includeTasks}
              onChange={setIncludeTasks}
              label={`Tasks (${tasks.length})`}
              hint="Task list with priority, status, due date, assignee"
            />
            <SectionToggle
              testId="toggle-pages"
              checked={includePages}
              onChange={setIncludePages}
              label={`Pages / Research (${pages.length})`}
              hint="Rich-text research / notes pages — often internal"
            />
            <SectionToggle
              testId="toggle-comments"
              checked={includeComments}
              onChange={setIncludeComments}
              label="Discussion thread"
              hint="Internal comments and @mentions on the project"
            />
          </div>

          {/* Per-task picker */}
          {includeTasks && tasks.length > 0 && (
            <div className="border border-slate-200 rounded-sm p-3" data-testid="tasks-picker">
              <div className="flex items-center justify-between mb-2">
                <div className="gc-label">Tasks to include</div>
                <div className="flex gap-2 text-[11px]">
                  <button onClick={() => toggleAll(setTaskSel, allTaskIds, true)} className="text-[#0a2540] hover:underline">Select all</button>
                  <span className="text-slate-300">·</span>
                  <button onClick={() => toggleAll(setTaskSel, allTaskIds, false)} className="text-slate-500 hover:underline">None</button>
                </div>
              </div>
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {tasks.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1.5 py-1 rounded-sm" data-testid={`pdf-task-${t.id}`}>
                    <Checkbox checked={!!taskSel[t.id]} onCheckedChange={(v) => setTaskSel((s) => ({ ...s, [t.id]: !!v }))} />
                    <span className="flex-1 truncate">{t.title}</span>
                    <span className="font-mono-data text-slate-400 text-[10px] shrink-0">{fmtDate(t.due_date, "")}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Per-page picker */}
          {includePages && pages.length > 0 && (
            <div className="border border-slate-200 rounded-sm p-3" data-testid="pages-picker">
              <div className="flex items-center justify-between mb-2">
                <div className="gc-label">Pages to include</div>
                <div className="flex gap-2 text-[11px]">
                  <button onClick={() => toggleAll(setPageSel, allPageIds, true)} className="text-[#0a2540] hover:underline">Select all</button>
                  <span className="text-slate-300">·</span>
                  <button onClick={() => toggleAll(setPageSel, allPageIds, false)} className="text-slate-500 hover:underline">None</button>
                </div>
              </div>
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {pages.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1.5 py-1 rounded-sm" data-testid={`pdf-page-${p.id}`}>
                    <Checkbox checked={!!pageSel[p.id]} onCheckedChange={(v) => setPageSel((s) => ({ ...s, [p.id]: !!v }))} />
                    <span className="flex-1 truncate">{p.title || "Untitled"}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {loading && <div className="text-xs text-slate-400">Loading project content…</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={download} data-testid="btn-download-pdf">
            <FileDown size={14} className="mr-1.5" /> Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionToggle({ checked, onChange, label, hint, testId }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer" data-testid={testId}>
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" />
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
      </div>
    </label>
  );
}
